<template>
  <!-- Table Container Card -->
  <b-card no-body>
    <div class="m-2">
      <!-- Table Top -->
      <b-row>
        <!-- Per Page -->
        <b-col
          cols="12"
          md="6"
          class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
        >
          <label>{{ $t("Entries") }}</label>
          <v-select
            v-model="perPage"
            :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
            :options="perPageOptions"
            :clearable="false"
            class="per-page-selector d-inline-block ml-50 mr-1"
          />
          <b-button
            v-if="getUserPermission('CREATE_USER')"
            variant="primary"
            @click="
              showModal('modal-lg'),
              (isReadonly = false),
              (modalType = { type: 'add', text: $t('Add New User') })
            "
          >
            {{ $t("Add") }}
          </b-button>
        </b-col>
        <b-col
          cols="12"
          md="6"
        >
          <div class="d-flex align-items-center justify-content-lg-end">
            <b-button
              variant="primary"
              class="mr-1"
              @click="showModal('modal-filter')"
            >
              <feather-icon icon="FilterIcon" /> {{ $t("Filter") }}
            </b-button>
            <Button
              :label="$t('Reset Filter')"
              size="md"
              icon="RotateCwIcon"
              show-icon
              @click="resetFilter"
            />
          </div>
        </b-col>
      </b-row>
    </div>

    <b-table
      :items="cachedUsersList ? cachedUsersList.data : users"
      responsive
      :busy="isBusy"
      :per-page="pageSize"
      :current-page="currentPage"
      :fields="translatedTableHeaders"
      primary-key="id"
      show-empty
      :empty-text="$t('No data available')"
      :sort-desc.sync="sortDesc"
      class="position-relative"
    >
      <template #table-busy>
        <div class="text-center text-danger my-2">
          <b-spinner
            class="align-middle"
            variant="primary"
          />
        </div>
      </template>
      <template #cell(SN)="data">
        {{ getOverallIndex(data.index) }}
      </template>
      <!-- Column: Role Name -->
      <template #cell(roleName)="data">
        {{ data.value }}
      </template>
      <template #cell(status)="data">
        <b-badge
          pill
          :variant="getStatusColor(data.value)"
        >
          {{ data.value }}
        </b-badge>
      </template>
      <!-- Column: Actions -->
      <template #cell(actions)="data">
        <div class="text-nowrap">
          <!-- Dropdown -->
          <b-dropdown
            variant="link"
            toggle-class="p-0"
            no-caret
            :right="$store.state.appConfig.isRTL"
          >
            <template #button-content>
              <feather-icon
                icon="MoreVerticalIcon"
                size="16"
                class="align-middle text-body"
              />
            </template>

            <b-dropdown-item
              v-if="getUserPermission('MODIFY_USER')"
              @click="onSelectRoleItem(data.item, 'edit')"
            >
              <feather-icon icon="EditIcon" />
              <span class="align-middle ml-50">{{ $t("Modify") }}</span>
            </b-dropdown-item>
            <b-dropdown-item @click="onSelectRoleItem(data.item, 'view')">
              <feather-icon icon="EyeIcon" />
              <span class="align-middle ml-50">{{ $t("View") }}</span>
            </b-dropdown-item>
            <b-dropdown-item
              v-if="
                getUserPermission('RESET_PASSWORD') &&
                  data.item.enable2FA &&
                  data.item.twoFactorOption === 'SOFTWARE'
              "
              @click="forgetAuthenticatorApp(data.item)"
            >
              <feather-icon icon="LogOutIcon" />
              <span class="align-middle ml-50">{{
                $t("Forget Authenticator App")
              }}</span>
            </b-dropdown-item>
            <b-dropdown-item
              v-if="
                getUserPermission(
                  data.item.status === 'Active'
                    ? 'DEACTIVATE_USER'
                    : 'ACTIVATE_USER'
                )
              "
              @click="editUserStatus(data.item, $event.target)"
            >
              <feather-icon icon="CheckSquareIcon" />
              <span class="align-middle ml-50">{{
                data.item.status === "Active"
                  ? $t("Deactivate")
                  : $t("Activate")
              }}</span>
            </b-dropdown-item>
            <b-dropdown-item
              v-if="getUserPermission('RESET_PASSWORD')"
              @click="resetPassword(data.item)"
            >
              <feather-icon icon="CheckSquareIcon" />
              <span class="align-middle ml-50">{{ $t("Reset Password") }}</span>
            </b-dropdown-item>
          </b-dropdown>
        </div>
      </template>
    </b-table>
    <TablePagination
      v-model="currentPage"
      :from="from"
      :to="to"
      :total-rows="totalEntries"
      :per-page="perPage"
    />
    <!-- modal -->
    <b-modal
      id="modal-lg"
      ref="my-modal"
      centered
      no-close-on-backdrop
      size="xl"
      :title="modalType.text"
      @hidden="clearForm"
    >
      <b-form @submit.prevent="handleSubmit">
        <validation-observer ref="userRules">
          <p class="font-weight-bold my-2">
            {{ $t("Personal Information") }}
          </p>
          <b-row class="match-height">
            <!-- First Name -->
            <b-col md="6">
              <InputField
                id="first-name"
                v-model="userFormData.firstname"
                :label="$t('First Name')"
                :placeholder="$t('Enter First Name')"
                type="text"
                :error-name="$t('First Name')"
                rules="required"
                :readonly="isReadonly"
                onpaste="return false;"
                :is-allow-special-characters="false"
              />
            </b-col>
            <!-- First Name -->
            <!-- Last Name -->
            <b-col md="6">
              <InputField
                id="last-name"
                v-model="userFormData.lastname"
                :label="$t('Last Name')"
                :placeholder="$t('Enter Last Name')"
                type="text"
                :error-name="$t('Last Name')"
                rules="required"
                :readonly="isReadonly"
                onpaste="return false;"
                :is-allow-special-characters="false"
              />
            </b-col>
            <!-- Last Name -->
            <!-- Other Name -->
            <b-col md="6">
              <InputField
                id="other-name"
                v-model="userFormData.othernames"
                :label="$t('Other Name')"
                :placeholder="$t('Enter Other Name')"
                type="text"
                :error-name="$t('Other Name')"
                for="other-name"
                :readonly="isReadonly"
                onpaste="return false;"
                :is-allow-special-characters="false"
              />
            </b-col>
            <!-- Other Name -->
            <!-- phonenumber -->
            <b-col md="6">
              <InputField
                id="phone-number"
                v-model="userFormData.phoneNumber"
                :label="$t('Phone Number')"
                :placeholder="$t('Enter Phone Number')"
                type="number"
                :error-name="$t('Phone-number')"
                rules="required|integer|min:7|max:11"
                onpaste="return false;"
                :is-allow-special-characters="false"
                :readonly="isReadonly"
              />
            </b-col>
            <!-- phonenumber -->
            <!-- Email -->
            <b-col md="6">
              <InputField
                id="email"
                ref="email"
                v-model="userFormData.email"
                :label="$t('Email')"
                :placeholder="$t('Enter Email')"
                type="email"
                vid="email"
                :error-name="$t('Email')"
                rules="required|email"
                :readonly="isReadonly || modalType.type === 'update'"
              />
            </b-col>
            <!-- Email -->
            <!-- Confirm Email -->
            <b-col
              v-if="modalType.type !== 'update'"
              md="6"
            >
              <InputField
                id="confirm-email"
                :label="$t('Confirm Email')"
                :placeholder="$t('Repeat Email')"
                type="email"
                onpaste="return false;"
                data-vv-as="email"
                rules="required|confirmed:email|email"
                :error-name="$t('Confirm Email')"
                :readonly="isReadonly || modalType.type === 'update'"
                :is-allow-copy-and-paste="false"
              />
            </b-col>
            <!-- Confirm Email -->
            <!-- address -->
            <b-col md="12">
              <TextAreaField
                id="address"
                v-model="userFormData.address"
                rows="2"
                :label="$t('Address')"
                :placeholder="$t('Enter Address')"
                type="text"
                :error-name="$t('Address')"
                rules="required"
                :readonly="isReadonly"
              />
            </b-col>
            <!-- address -->
          </b-row>
          <p class="font-weight-bold my-2">
            {{ $t("Employee Information") }}
          </p>
          <b-row class="match-height">
            <!-- Employee Number -->
            <b-col md="6">
              <InputField
                id="employee-number"
                v-model="userFormData.employeeNumber"
                :label="$t('Employee Number')"
                :placeholder="$t('Enter Employee Number')"
                type="text"
                rules="required"
                :error-name="$t('Employee Number')"
                :readonly="isReadonly"
              />
            </b-col>
            <!-- Employee Number -->
            <!-- Designation -->
            <b-col md="6">
              <InputField
                id="designation"
                v-model="userFormData.designation"
                :label="$t('Designation')"
                :placeholder="$t('Enter Designation')"
                type="text"
                rules="required"
                :error-name="$t('Designation')"
                :readonly="isReadonly"
              />
            </b-col>
            <!-- Designation -->
            <!-- branch -->
            <b-col md="6">
              <SelectField
                id="branch"
                v-model="userFormData.branchID"
                :label="$t('Branch')"
                :placeholder="$t('Select')"
                :options="allBranch"
                :reduce="(option) => option.value"
                :error-name="$t('Branch')"
                rules="required"
                item="label"
                :readonly="isReadonly"
              />
            </b-col>
            <!-- branch -->
            <!-- roles -->
            <b-col md="6">
              <SelectField
                id="role"
                v-model="userFormData.roleID"
                :label="$t('Role')"
                :placeholder="$t('Select')"
                :options="roleList"
                :reduce="(option) => option.value"
                :error-name="$t('Role')"
                rules="required"
                item="label"
                :readonly="isReadonly"
              />
            </b-col>
            <!-- roles -->
            <!-- access level -->
            <b-col md="6">
              <SelectField
                id="access-level"
                v-model="userFormData.accessLevel"
                :label="$t('Access Level')"
                :placeholder="$t('Select')"
                :options="translatedAccessLevel"
                :reduce="(option) => option.value"
                :error-name="$t('Access Level')"
                rules="required"
                item="label"
                :readonly="isReadonly"
              />
            </b-col>
            <!-- access level -->
            <!-- scope limit -->
            <b-col md="6">
              <SelectField
                id="scope-limit"
                v-model="userFormData.scopeLimit"
                :label="$t('Scope Limit')"
                :placeholder="$t('Select')"
                :options="translatedScopeLimit"
                :reduce="(option) => option.value"
                :error-name="$t('Scope Limit')"
                rules="required"
                item="label"
                :readonly="isReadonly"
              />
            </b-col>
            <!-- scope limit -->
          </b-row>
          <p class="font-weight-bold my-2">
            {{ $t("User System Information") }}
          </p>
          <b-row>
            <!-- <b-col md="3">
              <SwitchButton
                v-model="userFormData.canApprove"
                label="Can Approve?"
                name="can-approve"
                class="mt-2"
                :readonly="isReadonly"
              />
            </b-col> -->
            <b-col
              v-if="modalType.type === 'view'"
              md="3"
            >
              <SwitchButton
                v-model="userFormData.isLockedOut"
                :label="$t('Is Locked Out?')"
                name="is-locked-out"
                class="mt-2"
                :readonly="isReadonly"
              />
            </b-col>
            <b-col
              v-if="modalType.type !== 'add'"
              md="3"
            >
              <SwitchButton
                v-model="userFormData.enable2FA"
                :label="$t('Enable 2FA?')"
                name="enable-2fa"
                class="mt-2"
                :readonly="isReadonly"
                :on-change="setEnable2FA(userFormData.enable2FA)"
              />
            </b-col>
            <b-col
              v-if="isEnable2FA && modalType.type !== 'add'"
              md="3"
            >
              <SelectField
                id="two-factor"
                v-model="userFormData.twoFactorOption"
                :label="$t('Two Factor Type')"
                :placeholder="$t('Select')"
                :options="translatedTwoFactorOptions"
                :reduce="(option) => option.value"
                :error-name="$t('Two Factor Type')"
                rules="required"
                item="label"
                :readonly="isReadonly"
              />
            </b-col>
            <b-col
              v-if="modalType.type !== 'add'"
              md="3"
            >
              <SwitchButton
                v-model="userFormData.enableSystemAccess"
                :label="$t('Enable System Access?')"
                name="enable-system-access"
                class="mt-2"
                :readonly="isReadonly"
              />
            </b-col>
          </b-row>
          <div class="business-card">
            <p class="font-weight-bold my-2">
              {{ $t("Exclusive Function Permission") }}
            </p>
            <b-row>
              <b-col md="6">
                <SwitchButton
                  v-model="userFormData.hasPermissionExclusion"
                  :label="$t('Grant Exclusion Function')"
                  name="exclusive-function"
                  class="mt-2"
                  :readonly="isReadonly"
                />
              </b-col>
            </b-row>

            <app-collapse
              v-if="userFormData.hasPermissionExclusion"
              class="business-items"
              type="margin"
              accordion
            >
              <div
                v-if="isViewOnly"
                class="card-item pt-2"
              >
                <div class="ml-2 mb-1">
                  <h5>
                    {{ $t("Special Permissions") }}
                    <feather-icon
                      icon="AlertCircleIcon"
                      size="16"
                      class="cursor-pointer"
                      @click="onViewInfo"
                    />
                  </h5>
                </div>

                <div
                  v-for="(item, key) in assignedPermissions"
                  :key="key"
                  :title="key"
                  class="px-2"
                >
                  <b-row class="ml-0">
                    <b-col
                      v-for="(permission, j) in item"
                      :key="j"
                      md="3"
                      class="business-item mr-1 mb-1"
                    >
                      <div
                        class="d-flex align-items-center justify-content-between"
                      >
                        <b-form-checkbox
                          v-model="userData.permissions"
                          :value="permission.name"
                          :disabled="isReadonly"
                        >
                          {{ permission.description }}
                        </b-form-checkbox>
                      </div>
                    </b-col>
                  </b-row>
                </div>
              </div>
              <div v-else>
                <!-- Permission Selector -->
                <PermissionSelector
                  ref="permissionSelector"
                  v-model="userFormData.permissions"
                  :permissions="permissions"
                  :readonly="isReadonly"
                />
              </div>
              <!-- {{ userData.permissions }} -->
            </app-collapse>
          </div>
        </validation-observer>
      </b-form>
      <template #modal-footer="{ cancel }">
        <div v-if="isReadonly" />
        <template v-else>
          <b-button
            variant="outline-secondary"
            :disabled="isLoading"
            @click="cancel()"
          >
            {{ $t('Cancel') }}
          </b-button>
          <b-button
            variant="primary"
            :disabled="isLoading"
            @click="handleSubmit"
          >
            <b-spinner
              v-if="isLoading"
              small
              class="mr-50"
            />
            {{ $t('Save') }}
          </b-button>
        </template>
      </template>
    </b-modal>
    <!-- modal -->

    <!-- Filter modal -->
    <b-modal
      id="modal-filter"
      ref="modal-filter"
      :ok-title="$t('Apply')"
      :cancel-title="$t('Cancel')"
      cancel-variant="outline-secondary"
      centered
      no-close-on-backdrop
      size="lg"
      :title="$t('Filters')"
      modal-footer
      @ok="fetchUsers"
      @cancel="resetFilters"
    >
      <b-row class="match-height">
        <!-- Name  -->
        <b-col md="6">
          <InputField
            id="name"
            v-model="filter.name"
            :label="$t('Name')"
            :placeholder="$t('Enter name')"
            type="text"
            for="name"
            onpaste="return false;"
            :is-allow-special-characters="false"
          />
        </b-col>
        <!-- Name -->
        <!-- Email -->
        <b-col md="6">
          <InputField
            id="email"
            v-model="filter.email"
            :label="$t('Email')"
            :placeholder="$t('Enter Email')"
            type="email"
            for="email"
          />
        </b-col>
        <!-- Email -->
        <!-- phoneNumber -->
        <b-col md="6">
          <InputField
            id="phoneNumber"
            v-model="filter.phone"
            :label="$t('Phone Number')"
            :placeholder="$t('Enter Phone Number')"
            type="number"
            for="phoneNumber"
          />
        </b-col>
        <!-- phoneNumber -->
        <!-- branch -->
        <b-col md="6">
          <SelectField
            id="branch"
            v-model="filter.branchID"
            :label="$t('Branch')"
            :placeholder="$t('Select')"
            :options="allBranch"
            :reduce="(option) => option.value"
            :error-name="$t('Branch')"
            rules="required"
            item="label"
            :readonly="isReadonly"
          />
        </b-col>
        <!-- branch -->
        <!-- roles -->
        <b-col md="6">
          <SelectField
            id="role"
            v-model="filter.roleID"
            :label="$t('Role')"
            :placeholder="$t('Select')"
            :options="roleList"
            :reduce="(option) => option.value"
            :error-name="$t('Role')"
            rules="required"
            item="label"
            :readonly="isReadonly"
          />
        </b-col>
        <!-- roles -->
        <!-- access level -->
        <b-col md="6">
          <SelectField
            id="access-level"
            v-model="filter.accessLevel"
            :label="$t('Access Level')"
            :placeholder="$t('Select')"
            :options="translatedAccessLevel"
            :reduce="(option) => option.value"
            :error-name="$t('Access Level')"
            rules="required"
            item="label"
            :readonly="isReadonly"
          />
        </b-col>
        <!-- access level -->
        <!-- scope limit -->
        <b-col md="6">
          <SelectField
            id="scope-limit"
            v-model="filter.scopeLimit"
            :label="$t('Scope Limit')"
            :placeholder="$t('Select')"
            :options="translatedScopeLimit"
            :reduce="(option) => option.value"
            :error-name="$t('Scope Limit')"
            rules="required"
            item="label"
            :readonly="isReadonly"
          />
        </b-col>
        <!-- scope limit -->
      </b-row>
    </b-modal>
    <!-- Filter modal -->

    <b-modal
      id="info-modal"
      ref="infoModal"
      centered
      size="md"
      ok-only
      ok-title="Okay"
      @ok="hideModal('info-modal')"
    >
      <div class="p-2">
        <p>
          {{ $t("Special permissions are extra permissions given to users") }}
        </p>
      </div>
    </b-modal>

    <b-modal
      id="reset-password-modal"
      ref="resetPasswordModal"
      :header-bg-variant="headerBgVariant"
      :title="successMessage"
      cancel-variant="outline-secondary"
      centered
      no-close-on-backdrop
      size="md"
      :ok-title="$t('EMAIL PASSWORD')"
      :cancel-title="$t('Cancel')"
      modal-footer
      @ok="performEmailPassword"
      @cancel="hideModal('reset-password-modal')"
    >
      <div>
        <b-row>
          <b-col
            md="12"
            class="mt-2"
          >
            <InputPasswordField
              id="password"
              v-model="retrievedPassword"
              :label="$t('Password')"
              :placeholder="$t('Password')"
              :error-name="$t('Password')"
              rules="required"
              vid="password"
              readonly
              class="remove-border"
            />
          </b-col>
          <b-col md="12">
            <button
              class="btn btn-link p-0 mb-2"
              variant="primary"
              @click="copy2ClipBoard"
            >
              <span style="font-size: 12px; font-weight: bold; cursor: pointer">
                {{ labelTitle }}
              </span>
            </button>
          </b-col>
          <b-col md="12">
            <b-alert
              show
              variant="info"
            >
              <b-row>
                <b-col
                  md="1"
                  class="pt-1 pb-1 pl-3"
                >
                  <feather-icon
                    size="20"
                    icon="InfoIcon"
                  />
                </b-col>
                <b-col
                  md="9"
                  class="p-1"
                  style="text-align: justify; color: #555"
                >
                  {{
                    $t(
                      "Save time by letting users to recover their passwords using the"
                    )
                  }}
                  <router-link to="#">
                    {{ $t("Forgot Password Link") }}
                  </router-link>
                </b-col>
              </b-row>
            </b-alert>
          </b-col>
        </b-row>
      </div>
    </b-modal>
    <FullScreenSpinner :show="showSpinner" />
  </b-card>
</template>

<script>
import AppCollapse from '@core/components/app-collapse/AppCollapse.vue'
import { modalMixins } from '@core/mixins/modal'
import { permissionMixin } from '@core/mixins/permission'
import { sweetAlert } from '@core/mixins/sweetAlert'
import { tableMixins } from '@core/mixins/table'
import { required } from '@validations'
import {
  BAlert,
  BBadge,
  BButton,
  BCard,
  BCol,
  BDropdown,
  BDropdownItem,
  BForm,
  BFormCheckbox,
  BModal,
  BRow,
  BSpinner,
  BTable,
} from 'bootstrap-vue'
import { ValidationObserver } from 'vee-validate'
import vSelect from 'vue-select'
import { mapActions, mapGetters } from 'vuex'
import TablePagination from '@/views/components/table/TablePagination.vue'
import FullScreenSpinner from '@/views/components/spinner/FullScreenSpinner.vue'
import PermissionSelector from '@/views/components/permission/PermissionSelector.vue'
import getStatusColor from '../../../helpers/getStatusColor'
import { adminPermissions } from '../../../helpers/permissions'
import Button from '../../components/button/Button.vue'
import SwitchButton from '../../components/switch/Switch.vue'
import InputField from '../../forms/InputField.vue'
import InputPasswordField from '../../forms/InputPasswordField.vue'
import SelectField from '../../forms/SelectField.vue'
import TextAreaField from '../../forms/TextAreaField.vue'

const twoFactorOptions = [
  {
    label: 'EMAIL',
    value: 'EMAIL',
  },
  {
    label: 'SMS',
    value: 'SMS',
  },
  {
    label: 'Authenticator App',
    value: 'SOFTWARE',
  },
]

const scopeLimitOptions = [
  {
    label: 'Branch',
    value: 'Branch',
  },
  {
    label: 'Global',
    value: 'Global',
  },
]

const accessLevelOptions = [
  {
    label: 'Level 1',
    value: 1,
  },
  {
    label: 'Level 2',
    value: 2,
  },
  {
    label: 'Level 3',
    value: 3,
  },
  {
    label: 'Level 4',
    value: 4,
  },
  {
    label: 'Level 5',
    value: 5,
  },
]

export default {
  components: {
    BCard,
    BRow,
    BCol,
    BButton,
    Button,
    BTable,
    BSpinner,
    SwitchButton,
    BDropdown,
    BDropdownItem,
    BBadge,
    BModal,
    BAlert,
    BForm,
    vSelect,
    ValidationObserver,
    InputField,
    InputPasswordField,
    SelectField,
    BFormCheckbox,
    TextAreaField,
    AppCollapse,
    TablePagination,
    FullScreenSpinner,
    PermissionSelector,
  },
  mixins: [modalMixins, tableMixins, sweetAlert, permissionMixin],
  data() {
    return {
      headerBgVariant: 'primary',
      successMessage: '',
      sortBy: 'id',
      required,
      sortDesc: false,
      showSpinner: false,
      tableHeaders: [
        { key: 'SN', sortable: true, label: 'S/N' },
        { key: 'firstname', sortable: true, label: 'First Name' },
        { key: 'lastname', sortable: true, label: 'Last Name' },
        { key: 'email', sortable: true, label: 'Email' },
        { key: 'username', sortable: true, label: 'Username' },
        { key: 'phoneNumber', sortable: true, label: 'Phone Number' },
        { key: 'branch', sortable: true, label: 'Branch' },
        { key: 'role', sortable: true, label: 'Role' },
        { key: 'status', sortable: true, label: 'Status' },
        { key: 'actions', label: 'Actions' },
      ],
      userFormData: {
        hasPermissionExclusion: true,
        permissions: [],
      },
      roleList: [],
      permissions: {},
      assignedPermissions: [],
      specialPermissions: [],
      loading: false,
      isViewOnly: false,
      modalType: {
        type: '',
        text: '',
      },
      filter: {
        name: '',
        email: '',
        username: '',
        phone: '',
        role: '',
        branch: '',
        accessLevel: '',
        scopeLimit: '',
        status: '',
      },
      isEnable2FA: false,
      isReadonly: false,
      allBranch: [],
      users: [],
      userCache: {}, // { [cacheKey]: { data: [], totalCount: number } }
      cacheStale: false,
      userPermissions: [],
      getStatusColor,
      labelTitle: this.$t('COPY PASSWORD'),
      retrievedPassword: '',
      userId: '',
    }
  },
  computed: {
    ...mapGetters({
      userData: 'auth/getCurrentUser',
    }),
    ...mapGetters('tableCache', ['getTableCache']),
    cacheKey() {
      // Use page + perPage + filter as cacheKey
      return JSON.stringify({ page: this.currentPage, perPage: this.perPage, filter: this.filter })
    },
    cachedUsersList() {
      return this.getTableCache('users', this.cacheKey)
    },
    translatedTableHeaders() {
      return this.tableHeaders.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.key }))
    },
    translatedTwoFactorOptions() {
      return twoFactorOptions.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.value }))
    },
    translatedScopeLimit() {
      return scopeLimitOptions.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.value }))
    },
    translatedAccessLevel() {
      return accessLevelOptions.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.value }))
    },
  },
  watch: {
    currentPage(newValue) {
      this.currentPage = newValue
      this.serveCacheAndFetchUsers()
    },
    perPage(newValue) {
      this.perPage = newValue
      this.serveCacheAndFetchUsers()
    },
  },
  async mounted() {
    this.serveCacheAndFetchUsers()
    this.getRoles()
    await this.fetchAllPermission()
    this.fetchBranches()
  },
  methods: {
    ...mapActions({
      getPermissions: 'admin/getAllPermission',
      addUser: 'admin/addUser',
      editUser: 'admin/editUser',
      getAllRoles: 'admin/getAllRoles',
      getUsers: 'admin/getUsers',
      getAllBranches: 'operations/getAllBranches',
      updateUserStatus: 'admin/updateUserStatus',
      resetUserPassword: 'admin/resetUserPassword',
      disabledAuthenticator: 'admin/disabledAuthenticator',
      sendEmailPassword: 'admin/sendEmailPasswordAction',
    }),
    ...mapActions('tableCache', ['setTableCache', 'markTableCacheStale', 'clearTableCache']),

    async serveCacheAndFetchUsers() {
      const { cacheKey } = this
      const cache = this.getTableCache('users', cacheKey)

      if (cache && cache.data) {
        this.users = cache.data
        this.totalCount = cache.totalCount
        this.isBusy = false
        this.fetchUsers(true)
      } else {
        // No cache, show spinner and fetch
        this.isBusy = true
        await this.fetchUsers(false)
      }
    },
    onViewInfo() {
      this.$root.$emit('bv::show::modal', 'info-modal')
    },
    async editUserStatus(items) {
      const {
        id, firstname, lastname, branch, username, role, scopeLimit, designation,
      } = items
      const actionType = items.status === 'Active' ? 'Inactive' : 'Active'
      const action = items.status === 'Active' ? this.$t('Deactivate') : this.$t('Activate')
      const permission = items.status === 'Active' ? 'DEACTIVATE_USER' : 'ACTIVATE_USER'

      try {
        const updateStatus = async () => {
          this.showSpinner = true
          const statusValues = {
            id,
            name: `${firstname} ${lastname}`,
            branch,
            username,
            role,
            scopeLimit,
            designation,
          }
          const response = await this.updateUserStatus({ statusValues, actionType, permission })
          if (response.status === 200) {
            this.markTableCacheStale('users')
            this.fetchUsers(true)
            const requestToText = this.$t('Your request to')
            const text = this.$t('User was successful')
            this.alertSuccess({ text: `${requestToText} ${action} ${text}` })
            this.showSpinner = false
          }
        }
        const areYouSure = this.$t('Are you sure you want to')
        const userText = this.$t('this User?')
        this.confirm(updateStatus, { text: `${areYouSure} ${action} ${userText}` })
      } catch (error) {
        const requestToText = this.$t('Your request to')
        const reasonText = this.$t('User failed. Reason')
        this.alertError({ text: `${requestToText} ${action} ${reasonText}: ${error}` })
      } finally {
        this.showSpinner = false
      }
    },
    copy2ClipBoard() {
      navigator.clipboard.writeText(this.retrievedPassword)
        .then(() => {
          this.labelTitle = this.$t('PASSWORD COPIED')
        }).catch(
          e => {
            console.log(e)
          },
        )
    },
    async performEmailPassword() {
      try {
        const setEmailPassword = async () => {
          this.showSpinner = true
          const formData = {
            id: this.userId,
            password: this.retrievedPassword,
          }
          const response = await this.sendEmailPassword(formData)
          const { status } = response
          if (status === 200) {
            this.alertSuccess({ text: this.$t('Your request to Email Password was successful') })
            this.$root.$emit('bv::hide::modal', 'reset-password-modal')
            this.showSpinner = false
          }
        }
        this.confirm(setEmailPassword, { title: '', text: this.$t('Are you sure you want to Email Password?') })
      } catch (error) {
        error.response.data.errors.forEach(responseError => {
          const text = this.$t('Your request to Email Password failed. Reason')
          this.alertError({ text: `${text}: ${responseError.errorMessage}` })
        })
      } finally {
        this.showSpinner = true
      }
    },
    async resetPassword(items) {
      const { id } = items
      this.userId = id
      try {
        const resetPasswordStatus = async () => {
          this.showSpinner = true
          const response = await this.resetUserPassword({ id })
          const { status, data } = response
          if (status === 200 && data) {
            this.retrievedPassword = data.keyInstrument
            this.fetchUsers()
            this.successMessage = this.$t('Reset Password was successful')
            this.$root.$emit('bv::show::modal', 'reset-password-modal')
            this.showSpinner = false
          }
        }
        this.confirm(resetPasswordStatus, { title: '', text: this.$t('Are you sure you want to Reset this Password?') })
      } catch (error) {
        error.response.data.errors.forEach(responseError => {
          const resetText = this.$t('Your request to Reset Password failed. Reason')
          this.alertError({ text: `${resetText}: ${responseError.errorMessage}` })
        })
        this.showSpinner = false
      } finally {
        this.showSpinner = false
      }
    },
    async forgetAuthenticatorApp(items) {
      const { id } = items
      const disabledAuthApp = async () => {
        try {
          this.showSpinner = true
          const response = await this.disabledAuthenticator({ id })
          if (response.status === 200) {
            this.fetchUsers()
            this.alertSuccess({ text: this.$t('Your request to Reset User Authenticator App was successful') })
            this.showSpinner = false
          }
        } catch (error) {
          error.response.data.errors.forEach(responseError => {
            const reasonText = this.$t('Your request to Reset User Authenticator App failed. Reason')
            this.alertError({ text: `${reasonText}: ${responseError.errorMessage}` })
          })
        } finally {
          this.showSpinner = false
        }
      }
      this.confirm(disabledAuthApp, { title: '', text: this.$t('Are you sure you want to Reset User Authenticator App?') })
    },
    toggleModal() {
      // We pass the ID of the button that we want to return focus to
      // when the modal has hidden
      this.$refs['my-modal'].toggle('#toggle-btn')
    },

    setEnable2FA(value) {
      if (value) {
        this.isEnable2FA = true
      } else {
        this.isEnable2FA = false
        this.userFormData.twoFactorOption = ''
      }
    },

    async fetchAllPermission() {
      try {
        const response = await this.getPermissions({ permission: adminPermissions.VIEW_USER })
        const { status, data } = response
        if (status === 200 && data.data) {
          this.permissions = data.data.reduce((memo, curr) => {
          // eslint-disable-next-line no-param-reassign
            memo[curr.groupName] = Array.isArray(memo[curr.groupName])
              ? memo[curr.groupName].concat(curr)
              : [memo[curr.groupName]].concat(curr)
            return memo
          }, {})
        }
      } catch (error) {
        this.alertError({ text: error })
      } finally {
        this.isBusy = false
      }
    },
    async fetchBranches() {
      try {
        const response = await this.getAllBranches({ permission: adminPermissions.VIEW_USER })
        const { status, data } = response
        if (status === 200 && data.data) {
          this.allBranch = data.data.map(branch => ({
            label: branch.name,
            value: branch.id,
          // eslint-disable-next-line no-nested-ternary
          })).sort((a, b) => (a.label === b.label ? 0 : a.label < b.label ? -1 : 1))
        }
      } catch (error) {
        this.alertError({ text: error })
      }
    },
    async getRoles() {
      try {
        const response = await this.getAllRoles({ permission: adminPermissions.VIEW_USER })
        const { status, data } = response
        if (status === 200 && data.data) {
          this.roleList = data.data.map(role => ({
            label: role.name,
            value: role.id,
          // eslint-disable-next-line no-nested-ternary
          })).sort((a, b) => (a.label === b.label ? 0 : a.label < b.label ? -1 : 1))
        }
      } catch (error) {
        this.alertError({ text: error })
      }
    },
    async fetchUsers(isBackground = false) {
      if (!isBackground) {
        this.isBusy = true
        this.hideModal('modal-filter')
      }
      try {
        const response = await this.getUsers({
          search: this.filter,
          page: this.currentPage,
          limit: this.perPage,
          permission: adminPermissions.VIEW_USER,
        })
        const { status, data } = response
        if (status === 200 && data.data) {
          const cache = this.cachedUserList
          const isDifferent = !cache || JSON.stringify(cache.data) !== JSON.stringify(data.data)
          if (isDifferent) {
            this.users = data.data
            this.totalCount = data.totalItems
            // If on page 1 and data changed, mark all users cache as stale
            if (this.currentPage === 1) {
              this.markTableCacheStale('users')
            }
          }
          // Always update cache
          this.setTableCache({
            tableKey: 'users',
            cacheKey: this.cacheKey,
            payload: {
              data: data.data,
              totalCount: data.totalItems,
              lastFetched: Date.now(),
              stale: false,
            },
          })
        }
        this.cacheStale = false
      } catch (error) {
        this.alertError({ text: error })
      } finally {
        if (!isBackground) this.isBusy = false
      }
    },
    onSelectRoleItem(data, actionType) {
      if (actionType === 'view') {
        this.isViewOnly = true
        this.isReadonly = true
        // shows only special permissions
        const permission = Object.entries(this.permissions).reduce((memo, curr) => ({
          ...memo,
          [curr[0]]: curr[1].filter(item => item !== undefined && data.userPermissions.includes(item.name)),
        }), {})
        // eslint-disable-next-line no-unused-vars
        this.assignedPermissions = Object.fromEntries(Object.entries(permission).filter(([_, v]) => v.length !== 0))
        this.modalType = { type: 'view', text: this.$t('View User'), id: data.id }
      } else {
        this.isViewOnly = false
        this.isReadonly = false
        this.modalType = { type: 'update', text: this.$t('Edit User'), id: data.id }
      }

      this.toggleModal()
      const {
        phoneNumber,
        email,
        firstname,
        lastname,
        othernames,
        scopeLimit,
        branchID,
        roleID,
        enable2FA,
        isLockedOut,
        enableSystemAccess,
        twoFactorOption,
        employeeNumber,
        designation,
        address,
        accessLevel,
        userPermissions,
        hasPermissionExclusion,
      } = data

      this.userFormData = {
        phoneNumber,
        email,
        firstname,
        lastname,
        othernames,
        scopeLimit,
        branchID,
        roleID,
        permissions: actionType === 'view' ? userPermissions : (data.userPermissions || []),
        enable2FA,
        isLockedOut,
        enableSystemAccess,
        twoFactorOption,
        employeeNumber,
        designation,
        address,
        accessLevel,
        hasPermissionExclusion,
      }
    },
    handleSubmit(bvModalEvt) {
      if (bvModalEvt && bvModalEvt.preventDefault) {
        bvModalEvt.preventDefault()
      }
      this.$refs.userRules.validate().then(success => {
        if (success) {
          const areYouSure = this.$t('Are you sure you want to')
          const userText = this.$t('this User?')
          this.confirm(this.createUpdateUser, { text: `${areYouSure} ${this.modalType.type} ${userText}` })
        }
      })
    },
    resetFilters() {
      this.filter = { ...null }
      this.markTableCacheStale('users')
    },
    resetFilter() {
      this.filter = { ...null }
      this.markTableCacheStale('users')
      this.fetchUsers(true)
    },
    async createUpdateUser() {
      this.isLoading = true
      const formData = this.filterForm(this.userFormData)
      try {
        const response = this.modalType.type === 'add'
          ? await this.addUser(formData)
          : await this.editUser({ ...formData, id: this.modalType.id })
        if (response.status === 200) {
          const requestToText = this.$t('Your request to')
          const userText = this.$t('User was successful')
          this.alertSuccess({ text: `${requestToText} ${this.modalType.type} ${userText}` })
          this.fetchUsers(true)
        }
      } catch (error) {
        error.response.data.errors.forEach(responseError => {
          const requestToText = this.$t('Your request to')
          const reasonText = this.$t('User failed. Reason')
          this.alertError({ text: `${requestToText} ${this.modalType.type} ${reasonText}: ${responseError.errorMessage}` })
        })
      } finally {
        this.isLoading = false
        this.toggleModal()
        this.clearForm()
      }
    },
    filterForm(data) {
    // eslint-disable-next-line no-unused-vars
      return Object.fromEntries(Object.entries(data).filter(([_, value]) => value != null))
    },

    clearForm() {
      this.isLoading = false
      this.userFormData = {
        firstname: '',
        lastname: '',
        email: '',
        username: '',
        phoneNumber: '',
        notes: '',
        branchID: '',
        roleID: '',
        permissions: [],
        hasPermissionExclusion: true,
      }

      // Clear search in PermissionSelector
      if (this.$refs.permissionSelector) {
        this.$refs.permissionSelector.clearSearch()
      }

      this.isViewOnly = false
    },
  },
}
</script>

<style lang="scss" scoped>
.table-responsive {
  padding-bottom: 200px !important;
  margin-bottom: 0.5rem;
}
.card-item {
  border: 1px solid #d8d6de !important;
  border-radius: 0.358rem;
}
.remove-border {
  border: none;
  outline: none;
}
</style>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";

#reset-password-modal .modal-title {
  color: white;
  font-size: 16px;
}
</style>
